import moment from 'moment';
import { CorporateEventVendorStatusEnum, EventTypeFilterEnum } from '../../constants/enums';
import { IChat, IChatsRecord, IVendorChatTarget } from '../../interfaces/chats';
import { VendorEventStatusEnum } from '../../types/eventTypes';

export type EventMetaPillKind = 'upcoming' | 'today' | 'inProgress' | 'completed';

export interface EventMetaPill {
  kind: EventMetaPillKind;
  label: string;
}

const PILL_DATE_FORMAT = 'D MMM';

const parseDay = (value?: string | null) => {
  if (!value) return null;
  const day = moment(value, ['YYYY-MM-DD', moment.ISO_8601], true);
  return day.isValid() ? day.startOf('day') : null;
};

const isCompletedStatus = (event: Pick<IChatsRecord, 'eventType' | 'vendorEventStatus'>) =>
  event.eventType === EventTypeFilterEnum.B2B
    ? event.vendorEventStatus === CorporateEventVendorStatusEnum.Completed
    : event.vendorEventStatus === VendorEventStatusEnum.Completed;

export function getEventMetaPill(
  event: Pick<
    IChatsRecord,
    'eventType' | 'vendorEventStatus' | 'startDate' | 'endDate' | 'eventDate' | 'daysLeft' | 'archivedAt'
  >,
  options: { now?: Date } = {}
): EventMetaPill {

  const today = moment(options.now).startOf('day');
  const start = parseDay(event.startDate) ?? parseDay(event.eventDate);
  const end = parseDay(event.endDate) ?? start;

  if (isCompletedStatus(event) || Boolean(end && today.isAfter(end))) {
    return { kind: 'completed', label: end ? `Completed ${end.format(PILL_DATE_FORMAT)}` : 'Completed' };
  }

  if (start && end && !today.isBefore(start)) {
    return end.isAfter(start)
      ? { kind: 'inProgress', label: `In progress · ends ${end.format(PILL_DATE_FORMAT)}` }
      : { kind: 'today', label: 'Today' };
  }

  const daysLeft = start ? start.diff(today, 'days') : event.daysLeft;
  if (daysLeft <= 0) {
    return { kind: 'today', label: 'Today' };
  }
  return { kind: 'upcoming', label: `${daysLeft} ${daysLeft === 1 ? 'day' : 'days'} left` };
}

export function getEventChatTargets(event: Pick<IChatsRecord, 'chats'>): IVendorChatTarget[] {
  return event.chats.filter((chat) => Boolean(chat.id)).map((chat) => ({ chatId: chat.id, chatType: 'event' }));
}

type ReadStateChat = Pick<IChat, 'channelUrl' | 'isNewModelChannel' | 'isChatAccessible'>;

export function canMarkChatUnread(chat: ReadStateChat): boolean {
  return chat.isNewModelChannel === true && Boolean(chat.channelUrl) && chat.isChatAccessible !== false;
}

export function getMarkUnreadChats<T extends ReadStateChat>(chats: T[]): T[] {
  return chats.filter(canMarkChatUnread);
}

export function hasChatMessages(chat: Pick<IChat, 'lastMessage'>): boolean {
  return Boolean(chat.lastMessage);
}

export function hasUnreadChat(chat: Pick<IChat, 'unreadMessageCount' | 'hasUnreadMention'>): boolean {
  return Boolean(chat.unreadMessageCount || chat.hasUnreadMention);
}

export function getArchivedRowCount(unreadChatCount?: number | null): string {
  if (!unreadChatCount || unreadChatCount <= 0) return '';
  return unreadChatCount > 99 ? '99+' : String(unreadChatCount);
}
